import React, { useState, useEffect, useRef } from 'react'

import { CapacitorBarcodeScanner, CapacitorBarcodeScannerTypeHint } from '@capacitor/barcode-scanner'

import { useNavigate, Outlet, useLocation } from 'react-router-dom'

import { Box, BottomNavigation, BottomNavigationAction, Stack, CssBaseline } from '@mui/material'
import { Map, EvStation, QrCodeScanner as QrCodeScannerIcon, Wallet, Person } from '@mui/icons-material'

import { AppRoutes, PROTECTED_ROUTES } from '@/routes/types/routes'
import { GeolocationProvider } from '@/contexts/GeolocationContext'
import { useKeyboard } from '@/contexts/KeyboardContext'
import { useAuth } from '@/hooks/useAuth'
import { calcWithSafeAreaBottom } from '@/utils/layout'

const MainTemplate: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const { isKeyboardVisible } = useKeyboard()
  const { isAuthenticated } = useAuth()

  const [isSupported, setIsSupported] = useState<boolean>(true)
  const [isContentOverflow, setIsContentOverflow] = useState<boolean>(false)

  const mainRef = useRef<HTMLDivElement>(null)

  const scan = async () => {
    try {
      const result = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHint.QR_CODE,
      })

      if (result && result.ScanResult) {
        const slug = result.ScanResult.split(process.env.REACT_APP_URL).pop()

        if (slug) {
          navigate(slug)
        }
      }
    } catch {
      return
    }
  }

  const handleOnChangeBottomNavigation = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue === AppRoutes.SCAN) return

    const isProtected = PROTECTED_ROUTES.some((route) => newValue.includes(route))

    if (!isAuthenticated && isProtected) {
      if (location.pathname === AppRoutes.AUTH) return

      navigate(AppRoutes.AUTH, { state: { from: { pathname: newValue } } })
    } else {
      navigate(newValue)
    }
  }

  const checkContentOverflow = () => {
    if (mainRef.current) {
      const contentHeight = mainRef.current.scrollHeight
      const visibleHeight = mainRef.current.clientHeight - 112

      setIsContentOverflow(contentHeight > visibleHeight)
    }
  }

  useEffect(() => {
    checkContentOverflow()
  }, [location])

  useEffect(() => {
    const currentMain = mainRef.current

    if (!currentMain) return

    const handleContentChange = () => {
      const contentHeight = currentMain.scrollHeight
      const visibleHeight = currentMain.clientHeight - 112

      setIsContentOverflow(contentHeight > visibleHeight)
    }

    const observer = new ResizeObserver(handleContentChange)
    observer.observe(currentMain)

    window.addEventListener('resize', handleContentChange)

    handleContentChange()

    return () => {
      observer.unobserve(currentMain)
      observer.disconnect()
      window.removeEventListener('resize', handleContentChange)
    }
  }, [])

  return (
    <GeolocationProvider>
      <Stack sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <CssBaseline />

        <Box
          component="main"
          ref={mainRef}
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            marginBottom: isContentOverflow ? calcWithSafeAreaBottom(isKeyboardVisible ? '56px' : '112px') : 0,
          }}
        >
          <Outlet />
        </Box>

        {!isKeyboardVisible && (
          <Box
            component="footer"
            position="fixed"
            sx={{
              bottom: calcWithSafeAreaBottom('56px'),
              left: '50%',
              transform: 'translate(-50%, 50%)',
              paddingX: '24px',
              width: '100%',
              zIndex: 1,
              transition: 'transform 0.3s ease, opacity 0.3s ease',
            }}
          >
            <BottomNavigation
              value={location.pathname}
              onChange={handleOnChangeBottomNavigation}
              showLabels
              sx={{
                borderRadius: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                boxShadow: 2,
              }}
            >
              <BottomNavigationAction
                label="Mapa"
                value="/"
                icon={<Map />}
                disableRipple
                sx={{
                  minWidth: '50px',
                }}
              />
              <BottomNavigationAction
                label="Estações"
                value="/stations"
                icon={<EvStation />}
                disableRipple
                sx={{
                  minWidth: '50px',
                }}
              />
              <BottomNavigationAction
                value="scan"
                icon={<QrCodeScannerIcon />}
                disableRipple
                sx={{
                  color: 'white',
                  backgroundColor: isSupported ? 'primary.main' : 'action.disabled',
                  borderRadius: '50%',
                  minWidth: 'auto',
                  width: 44,
                  height: 44,
                  padding: 0,
                  margin: '6px',
                  flex: 'none',
                  '&.Mui-selected': {
                    color: 'white',
                  },
                }}
                onClick={scan}
                disabled={!isSupported}
              />
              <BottomNavigationAction
                label="Carteira"
                value="/wallet"
                icon={<Wallet />}
                disableRipple
                sx={{
                  minWidth: '50px',
                }}
              />
              <BottomNavigationAction
                label="Perfil"
                value="/profile"
                icon={<Person />}
                disableRipple
                sx={{
                  minWidth: '50px',
                }}
              />
            </BottomNavigation>
          </Box>
        )}
      </Stack>
    </GeolocationProvider>
  )
}

export default MainTemplate
